"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DatePickerInput = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _objectWithoutProperties2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutProperties"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _constants = require("../../constants");
var _DatePicker = require("../DatePicker/DatePicker");
var _Dropdown = require("../Dropdown/Dropdown");
var _DropdownPopper = require("../DropdownPopper/DropdownPopper");
var _FormField = require("../FormField/FormField.context");
var _Input = require("../Input/Input");
var _excluded = ["datepicker", "value", "defaultValue", "onChange", "id", "inputDelay", "onKeyDown"];
/**
 * A date picker input is an Input field with a DatePicker as a Dropdown. You can compose this
 * component as you would a normal Input (ex: inside of an AdornedInput).
 *
 * **Note:** The date picker input does not validate values. Values selected from the date picker
 * dropdown will follow the `dateFormat` provided to DatePicker, but the input
 * itself does not prevent the user from entering invalid values. You can use the
 * [date-nfs](https://date-fns.org/) library for any validation logic.
 *
 * @related [`DatePicker`]
 * @since 8.0.0
 */
var DatePickerInput = /*#__PURE__*/(0, _react.forwardRef)(function (_ref, forwardedRef) {
  var datepicker = _ref.datepicker,
    controlledValue = _ref.value,
    defaultValue = _ref.defaultValue,
    _onChange = _ref.onChange,
    id = _ref.id,
    inputDelay = _ref.inputDelay,
    onKeyDown = _ref.onKeyDown,
    rest = (0, _objectWithoutProperties2.default)(_ref, _excluded);
  var _useState = (0, _react.useState)(defaultValue),
    _useState2 = (0, _slicedToArray2.default)(_useState, 2),
    uncontrolledValue = _useState2[0],
    setUncontrolledValue = _useState2[1];
  var _useState3 = (0, _react.useState)(false),
    _useState4 = (0, _slicedToArray2.default)(_useState3, 2),
    isDropdownOpen = _useState4[0],
    setIsDropdownOpen = _useState4[1];
  var value = typeof controlledValue !== 'undefined' ? controlledValue : uncontrolledValue;
  var _useState5 = (0, _react.useState)(value),
    _useState6 = (0, _slicedToArray2.default)(_useState5, 2),
    calendarValue = _useState6[0],
    setCalendarValue = _useState6[1];
  var context = (0, _react.useContext)(_FormField.FormFieldContext);
  var isReadOnly = typeof rest.readOnly !== 'undefined' ? rest.readOnly : context.readOnly;
  var calendarRef = (0, _react.useRef)();
  var innerRef = (0, _react.useRef)();
  var inputRef = forwardedRef || innerRef;
  var inputTimeout = (0, _react.useRef)();
  (0, _react.useEffect)(function () {
    setCalendarValue(value);
  }, [value]);
  var onInputChangeCallback = (0, _react.useCallback)(function (e) {
    var changed = e.target.value;
    setUncontrolledValue(changed);
    if (_onChange) {
      _onChange(changed, {
        reason: _constants.DATE_PICKER_CHANGE_REASON.MANUAL_INPUT
      });
    }
    // create a debounce so calendar could delay its response
    // to input change
    clearTimeout(inputTimeout.current);
    inputTimeout.current = setTimeout(function () {
      setCalendarValue(changed);
    }, inputDelay);
  }, [inputDelay, _onChange]);
  var onOpenCallback = (0, _react.useCallback)(function () {
    setIsDropdownOpen(true);
  }, []);
  var onCloseCallback = (0, _react.useCallback)(function () {
    setIsDropdownOpen(false);
  }, []);
  var onKeyDownCallback = (0, _react.useCallback)(function (e) {
    // Close dropdown on tab
    if (e.keyCode === _constants.KEY_CODES.TAB) {
      setIsDropdownOpen(false);
    }

    // Open the dropdown and prevent up/down from scrolling the page
    if (e.keyCode === _constants.KEY_CODES.ARROW_DOWN || e.keyCode === _constants.KEY_CODES.ARROW_UP) {
      setIsDropdownOpen(true);
      if (calendarRef && calendarRef.current) {
        calendarRef.current.focus();
      }
      e.preventDefault();
    }

    // Ignore enter if dropdown is open
    if (e.keyCode === _constants.KEY_CODES.ENTER) {
      if (isDropdownOpen) {
        setIsDropdownOpen(false);
        e.preventDefault();
      }
    }
    if (typeof onKeyDown === 'function') {
      onKeyDown(e);
    }
  }, [isDropdownOpen, onKeyDown]);
  var dropdown = /*#__PURE__*/_react.default.createElement(_Dropdown.Dropdown, {
    shouldIgnoreTriggerClick: true,
    shouldReturnFocus: false,
    onESCKeyPress: function onESCKeyPress() {
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, (0, _react.useCallback)(function (_ref2) {
    var onClose = _ref2.onClose;
    return /*#__PURE__*/(0, _react.cloneElement)(datepicker, {
      margin: 'sm',
      onChange: function onChange(e, meta) {
        var changed = e.formatted;
        setUncontrolledValue(changed);
        setCalendarValue(changed);
        if (_onChange) {
          _onChange(changed, meta);
        }
        if (inputRef && inputRef.current) {
          inputRef.current.focus();
        }
        onClose();
      },
      selected: calendarValue,
      ref: calendarRef
    });
  }, [calendarValue, datepicker, inputRef, _onChange]));
  return /*#__PURE__*/_react.default.createElement(_DropdownPopper.DropdownPopper, {
    id: id,
    shouldFocusTriggered: false,
    triggered: dropdown,
    openWith: isReadOnly ? [] : ['click', 'focus'],
    onClose: onCloseCallback,
    onOpen: onOpenCallback,
    isOpen: isDropdownOpen,
    role: "combobox"
  }, /*#__PURE__*/_react.default.createElement(_Input.Input, (0, _extends2.default)({
    ref: inputRef,
    value: value || '',
    onChange: onInputChangeCallback,
    onKeyDown: onKeyDownCallback
  }, rest)));
});
exports.DatePickerInput = DatePickerInput;
DatePickerInput.displayName = 'DatePickerInput';
DatePickerInput.propTypes = process.env.NODE_ENV !== "production" ? {
  'aria-autocomplete': _propTypes.default.string,
  'aria-label': _propTypes.default.string,
  'id': _propTypes.default.string,
  /**
   * You can customize the `DatePicker` by providing your own component.
   */
  'datepicker': _propTypes.default.node,
  /**
   * The value when using this as a
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   */
  'value': _propTypes.default.string,
  /**
   * The default value when using this as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   */
  'defaultValue': _propTypes.default.string,
  /**
   * Callback function when a day is selected from the calendar. It will receive a formatted date
   * and a meta object.
   *
   * **Note:** The value is not validated. Values selected from the date picker
   * dropdown will follow the `dateFormat` provided to DatePicker, but the input
   * itself does not prevent the user from entering invalid values. You can use the
   * [date-nfs](https://date-fns.org/) library for any validation logic.
   */
  'onChange': _propTypes.default.func,
  /**
   * Displays the input in a read-only state. Can also be inherited from a FormField parent.
   *
   * @since 8.31.0
   */
  'readOnly': _propTypes.default.bool,
  /**
   * This is the delay in milliseconds between keystrokes after which the
   * `DatePicker` will receive the input value and display the date in the calendar.
   *
   * @since 8.31.0
   */
  'inputDelay': _propTypes.default.number,
  /**
   * @ignore
   */
  'onKeyDown': _propTypes.default.func
} : {};
DatePickerInput.defaultProps = {
  'aria-autocomplete': 'none',
  'aria-label': 'Choose Date',
  'datepicker': /*#__PURE__*/_react.default.createElement(_DatePicker.DatePicker, null),
  'inputDelay': 500
};